import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import TriskelionMark from "@/components/TriskelionMark";
import { T } from "@/constants/testIds";

export default function AuthPanel({ title = "Sign in or create an account", subtitle = "Browsing is free — you only need an account to buy." }) {
  const { login, register } = useAuth();
  const [tab, setTab] = useState("signin");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      if (tab === "register") {
        if (password.length < 8) throw new Error("Password must be at least 8 characters");
        await register(name, email, password);
      } else {
        await login(email, password);
      }
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-md px-4 py-16">
      <form onSubmit={submit} className="border border-stone-200 bg-white p-8">
        <div className="mb-8 flex flex-col items-center gap-3 text-center">
          <img src="/logo.png" alt="V.I.A logo" className="h-14 w-14 object-cover" />
          <h1 className="font-display text-3xl font-black uppercase tracking-wide text-stone-950">{title}</h1>
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-stone-400">{subtitle}</p>
        </div>

        <div className="mb-6 flex border border-stone-200">
          <button
            type="button"
            data-testid={T.authTabSignin}
            onClick={() => setTab("signin")}
            className={`flex-1 py-2.5 font-mono text-[11px] font-bold uppercase tracking-[0.2em] transition-colors ${
              tab === "signin" ? "bg-stone-950 text-white" : "text-stone-500 hover:text-stone-900"
            }`}
          >
            Sign in
          </button>
          <button
            type="button"
            data-testid={T.authTabRegister}
            onClick={() => setTab("register")}
            className={`flex-1 py-2.5 font-mono text-[11px] font-bold uppercase tracking-[0.2em] transition-colors ${
              tab === "register" ? "bg-stone-950 text-white" : "text-stone-500 hover:text-stone-900"
            }`}
          >
            Create account
          </button>
        </div>

        <div className="space-y-4">
          {tab === "register" && (
            <div>
              <p className="via-label">Full name</p>
              <input
                data-testid={T.authRegisterName}
                className="via-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Juan Karran"
              />
            </div>
          )}
          <div>
            <p className="via-label">Email</p>
            <input
              data-testid={T.adminLoginEmail}
              type="email"
              className="via-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
            />
          </div>
          <div>
            <p className="via-label">Password {tab === "register" && "(min 8 characters)"}</p>
            <input
              data-testid={T.adminLoginPassword}
              type="password"
              className="via-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
            />
          </div>
        </div>

        {error && (
          <p data-testid={T.authError} className="mt-4 border border-[#E52E39]/40 bg-[#E52E39]/10 px-3 py-2 text-xs text-[#E52E39]">
            {error}
          </p>
        )}

        <button
          data-testid={tab === "register" ? T.authRegisterSubmit : T.adminLoginSubmit}
          type="submit"
          disabled={busy}
          className="mt-6 w-full bg-[#E52E39] py-3.5 font-display text-lg font-black uppercase tracking-wide text-white transition-colors hover:bg-[#c9202a] disabled:opacity-60"
        >
          {busy ? "One moment…" : tab === "register" ? "Create my account" : "Sign in"}
        </button>
        <p className="mt-4 text-center font-mono text-[10px] uppercase leading-relaxed tracking-widest text-stone-400">
          Your delivery details are saved so checkout is one click next time
        </p>
      </form>
    </div>
  );
}
